import { useContext, useEffect, useState } from "react";
import { AppStateContext } from "../../context/AppContext";
import { AuthContext } from "../../context/AuthContext";
import useApi from "../../hooks/useApi";
import {
  ListNotification,
  changeNotificationStatus,
} from "../../helper/API/API";
import useFilter from "../../hooks/useFilter";
import { CustomFilterDropdown } from "../../components/CustomFilter";
import {
  Button,
  DatePicker,
  Input,
  Modal,
  Space,
  Table,
  Row,
  Col,
  Card,
  Form,
  message,
  Select,
  Upload,
  InputNumber,
  Checkbox,
  Tabs,
} from "antd";
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import callApi from "../../helper/API/CallApi";
import { Severty, ShowToast } from "../../helper/toast";
import moment from "moment";
import { useDebouncedFunction } from "../../hooks/useDebounce";
import { uploadFileToS3 } from "../../helper/uploadImage";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { t } from "i18next";
import { PencilIcon } from "lucide-react";
import LocationMapPicker from "../../components/LocationMapPicker";
import { MailOutlined } from "@ant-design/icons";
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const { TextArea } = Input;
const { Option } = Select;

function SubAdminManagement() {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Sub Admin Management");

  return (
    <div>
      <div className="page-inner-headding">
        <UserManagement />
      </div>
    </div>
  );
}

function UserManagement() {
  const { request } = useRequest();
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [userVisible, setUserVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [filters, setFilters] = useFilter({
    sortField: "created_at",
    sort: "desc",
    page: 1,
    limit: 10,
  });

  const getUsers = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      if (filters.page) query.append("page", filters.page);
      if (filters.limit) query.append("limit", filters.limit);
      if (filters.search) query.append("search", filters.search);
      if (filters.isActive !== undefined && filters.isActive !== "")
        query.append("isActive", filters.isActive);
      if (filters.startDate) query.append("start_date", filters.startDate);
      if (filters.endDate) query.append("end_date", filters.endDate);

      const url = `${apiPath.subAdminList}?${query.toString()}`;
      request({
        url,
        method: "GET",
        onSuccess: ({ data }) => {
          setUserList(data?.list?.docs || []);
          setTotalDocs(data?.totalDocs || 0);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || "Failed to fetch users", Severty.ERROR);
          setLoading(false);
        },
      });
    } catch (error) {
      ShowToast(error.message, Severty.ERROR);
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, [JSON.stringify(filters), refresh]);

  const userColumns = [
    {
      title: "Employee ID",
      dataIndex: "suid",
      key: "suid",
      ellipsis: true,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Strength",
      dataIndex: "strength",
      key: "strength",
    },
    {
      title: "Acceptable Limit (m)",
      dataIndex: "totalArea",
      key: "totalArea",
      width: 130,
      align: "center",
      render: (area) => (area != null ? area : "—"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Button
          type={record?.isActive ? "primary" : "default"}
          size="small"
          onClick={() => {
            request({
              url: apiPath.updateSubAdminStatus,
              method: "PUT",
              data: { _id: record._id },
              onSuccess: ({ message: msg }) => {
                ShowToast(msg || "Status updated", Severty.SUCCESS);
                setRefresh(new Date().getTime());
              },
              onError: (err) => {
                ShowToast(err?.message || "Failed to update status", Severty.ERROR);
              },
            });
          }}
        >
          {record?.isActive ? "Active" : "Inactive"}
        </Button>
      ),
    },
    // {
    //   title: "Created At",
    //   dataIndex: "created_at",
    //   key: "created_at",
    //   render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    // },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingUser(record);
              setUserVisible(true);
            }}
            icon={<PencilIcon />}
          >
            Edit
          </Button>
    
          <Button
            type="link"
            size="small"
            icon={<MailOutlined />}
            onClick={() => {
              Modal.info({
                content: (
                  <div>
                    <p><strong>Name:</strong> {record?.name}</p>
                    <p><strong>Email:</strong> {record?.email || "Not available"}</p>
                  </div>
                ),
                okText: "Close",
              });
            }}
          >
            Email
          </Button>
        </div>
      ),
    }
  ];

  const handleUserTableChange = ({ current, pageSize }) => {
    setFilters((prev) => ({ ...prev, page: current, limit: pageSize }));
  };

  const debouncedUserSearch = useDebouncedFunction((term) => {
    setFilters((prev) => ({ ...prev, search: term, page: 1 }));
  }, 500);

  const handleUserDateRange = (dates, dateStrings) => {
    const [startDate, endDate] = dateStrings;
    setFilters((prev) => ({ ...prev, startDate, endDate, page: 1 }));
  };

  return (
    <Row gutter={[24, 0]}>
      <Col xs={24} xl={24}>
        <Card bordered={false} className="criclebox tablespace mb-24" title="Sub Admin Management">
          <div className="tab_inner_tit">
            <div className="tab-upload-wrap d-flex align-items-center justify-content-between flex-wrap">
              <h3>Sub Admin List (created by you)</h3>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Input.Search
                  placeholder="Search by name"
                  className="searchInput"
                  onSearch={(v) => setFilters((p) => ({ ...p, search: v, page: 1 }))}
                  onChange={(e) => debouncedUserSearch(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setUserVisible(true)}>
                  Add Sub Admin
                </Button>
              </div>
            </div>
          </div>
          <div className="table-responsive customPagination">
            <Table
              dataSource={userList}
              columns={userColumns}
              pagination={{
                pageSize: filters.limit,
                current: filters.page,
                total: totalDocs || 0,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
              }}
              onChange={handleUserTableChange}
              rowKey={(r) => r._id}
              loading={loading}
            />
          </div>
        </Card>
      </Col>
      <UserAddModal
        visible={userVisible}
        setVisible={setUserVisible}
        refresh={() => setRefresh(new Date().getTime())}
        editingUser={editingUser}
        setEditingUser={setEditingUser}
      />
    </Row>
  );
}

function UserAddModal({ visible, setVisible, refresh, editingUser, setEditingUser }) {
  const { userProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [profileFile, setProfileFile] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [selectedReporterId, setSelectedReporterId] = useState(null);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const currentType = userProfile?.type || userProfile?.userType;
  const currentId = userProfile?._id || userProfile?.id;
  /** Admin's user-list API returns SubAdmins only, so self is never in adminList — build option from profile. SuperAdmin: only Admins can be reporters. */
  const reporterSelectOptions =
    currentType === "Admin" && currentId
      ? [
          {
            value: currentId,
            label: `${userProfile?.name || "Admin"}${
              userProfile?.email ? ` (${userProfile.email})` : ""
            }`,
          },
        ]
      : adminList
          .filter((a) => (a.userType || a.type) === "Admin")
          .map((a) => ({
            value: a._id || a.id,
            label: `${a.name || a.email || ""}${
              a.email ? ` (${a.email})` : ""
            }`.trim(),
          }));

  useEffect(() => {
    if (visible) {
      request({
        url: `${apiPath.adminList}?limit=200`,
        method: "GET",
        onSuccess: ({ data }) => {
          const list = data?.list?.docs || data?.docs || [];
          setAdminList(list);
        },
      });
    }
  }, [visible]);

  useEffect(() => {
    if (visible && editingUser) {
      const rid = editingUser.reporterId?._id || editingUser.reporterId;
      setSelectedReporterId(rid || null);
      form.setFieldsValue({
        name: editingUser.name,
        email: editingUser.email,
        mobileNumber: editingUser.mobileNumber,
        userType: editingUser.userType || "user",
        locationData: {
          location: editingUser.location || "",
          latitude: editingUser.latitude,
          longitude: editingUser.longitude,
        },
        profilePic: editingUser.profilePic,
        isActive: editingUser.isActive !== false,
        strength: editingUser.strength ?? 0,
        totalArea: editingUser.totalArea || 100,
        suid: editingUser.suid,
        reporterId: rid,
      });
      if (editingUser.profilePic) {
        setProfileFile({
          uid: "-1",
          name: "profile",
          status: "done",
          url: editingUser.profilePic,
          thumbUrl: editingUser.profilePic,
        });
      } else setProfileFile(null);
    } else {
      setSelectedReporterId(null);
      if (currentType === "Admin" && currentId) {
        form.setFieldsValue({ reporterId: currentId });
        setSelectedReporterId(currentId);
      }
    }
    if (!visible) {
      form.resetFields();
      setEditingUser && setEditingUser(null);
      setProfileFile(null);
      setSelectedReporterId(null);
    }
  }, [visible, editingUser, currentType, currentId]);

  const handleProfileUpload = async ({ file }) => {
    setLoading(true);
    try {
      const data = await uploadFileToS3({ file, dirName: "admin/users" });
      const url = data?.location || data?.Location || data?.LocationName || null;
      const fileData = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url,
        thumbUrl: url,
      };
      setProfileFile(fileData);
      form.setFieldsValue({ profilePic: url });
      return fileData;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async (values) => {
    setLoading(true);
    const loc = values.locationData || {};
    const payload = {
      name: values.name,
      email: values.email,
      password: values.password,
      suid: values.suid,
      mobileNumber: values.mobileNumber || null,
      userType: values.userType || "user",
      location: loc.location || null,
      latitude: loc.latitude != null ? loc.latitude : undefined,
      longitude: loc.longitude != null ? loc.longitude : undefined,
      profilePic: values.profilePic || null,
      isActive: values.isActive !== false,
      strength: values.strength ?? 0,
      totalArea: values.totalArea || 100,
      reporterId: values.reporterId || undefined,
    };

    if (editingUser?._id) {
      payload._id = editingUser._id;
      if (values.newPassword?.trim()) payload.newPassword = values.newPassword;
      request({
        url: apiPath.updateSubAdmin,
        method: "PUT",
        data: payload,
        onSuccess: ({ message: msg }) => {
          ShowToast(msg, Severty.SUCCESS);
          form.resetFields();
          refresh();
          setVisible(false);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || "Failed to update user", Severty.ERROR);
          setLoading(false);
        },
      });
    } else {
      request({
        url: apiPath.addSubAdmin,
        method: "POST",
        data: payload,
        onSuccess: ({ message: msg }) => {
          ShowToast(msg, Severty.SUCCESS);
          form.resetFields();
          refresh();
          setVisible(false);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err?.message || err?.data?.message || "Failed to add user", Severty.ERROR);
          setLoading(false);
        },
      });
    }
  };

  const hide = () => {
    setVisible(false);
    form.resetFields();
    setProfileFile(null);
    setEditingUser && setEditingUser(null);
  };

  return (
    <Modal
      open={visible}
      title={editingUser ? "Edit Sub Admin" : "Add Sub Admin"}
      okText="Save"
      onCancel={hide}
      confirmLoading={loading}
      okButtonProps={{ form: "userForm", htmlType: "submit" }}
    >
      <Form id="userForm" form={form} onFinish={handleOk} layout="vertical">
        <Form.Item label="Reporter (Admin)" name="reporterId" rules={[{ required: true, message: "Select reporter!" }]}>
          <Select
            placeholder="Select Admin"
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={(v) => setSelectedReporterId(v || null)}
            options={reporterSelectOptions}
          />
        </Form.Item>
        <Form.Item
          label="Strength"
          name="strength"
          rules={[
            { required: true, message: "Enter strength" },
            {
              validator: (_, value) => {
                const rid = form.getFieldValue("reporterId") || selectedReporterId;
                const ridStr = rid != null ? String(rid) : "";
                let max = 0;
                if (currentType === "Admin" && currentId && String(currentId) === ridStr) {
                  max = Number(userProfile?.strength) || 0;
                } else {
                  const admin = adminList.find(
                    (a) => String(a._id || a.id) === ridStr
                  );
                  max = admin ? Number(admin.strength) || 0 : 0;
                }
                if (value == null || value === "") return Promise.resolve();
                const n = Number(value);
                if (n < 0) return Promise.reject(new Error("Strength cannot be negative"));
                if (n > max) return Promise.reject(new Error(`Cannot exceed reporter's strength (max ${max})`));
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber min={0} placeholder="0" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Employee ID" name="suid" disabled={!!editingUser}>
          <Input placeholder="Enter Employee ID" disabled={!!editingUser}/>
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter name!" }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter email!" },
            { type: "email", message: "Invalid email!" },
          ]}
        >
          <Input placeholder="Enter email" disabled={!!editingUser} />
        </Form.Item>
        {!editingUser ? (
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please enter password!" }]}
          >
            <Input.Password placeholder="Create password" />
          </Form.Item>
        ) : (
          <Form.Item label="New Password" name="newPassword">
            <Input.Password placeholder="Enter new password (optional)" />
          </Form.Item>
        )}
        <Form.Item label="Mobile" name="mobileNumber">
          <Input placeholder="Enter mobile number" />
        </Form.Item>
        <Form.Item label="Acceptable Limit (out of location)" name="totalArea">
          <Select placeholder="Select acceptable limit" style={{ width: "100%" }}>
            {[100, 200, 300, 400, 500].map((value) => (
              <Select.Option key={value} value={value}>
                {value} meters
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Location" name="locationData" initialValue={{ location: "", latitude: undefined, longitude: undefined }}>
          <LocationMapPicker />
        </Form.Item>
        <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
          <Checkbox>Active</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default SubAdminManagement;
